import { Card, Row, Table } from "antd";
import { useGetTableColumns } from "./hooks/useGetTableColumns";
import { Loader } from "../../../../../../../../components/Loader";
import { useFetchData } from "./hooks/useFetchData";
import { IPayeeDisbursements } from "../../../../../../../../types/PayeeDisbursements";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useOpenSideBar } from "./hooks/useOpenSideBar";
import { TRecord } from "./types";
import { useDefualtOnRow } from "../../../../../../../../lib/hooks/useDefaultOnRow";
import { Header } from "./components/Header";
import { useNoCountPaginationProps } from "../../../../../../../../lib/hooks/useNoCountPaginationProps";

type TPayeeDisbursements = {
  accountId: string;
  primaryBuyerNumber: number;
  primarySellerNumber: number;
  shouldPayeeDisbUpdate: boolean;
  isPassthru: boolean;
  setShouldPayeeDisbUpdate: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccess: () => void;
  sidebarIsOpen: boolean;
  setSidebarIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PayeeDisbursements = ({
  accountId,
  primaryBuyerNumber,
  primarySellerNumber,
  shouldPayeeDisbUpdate,
  isPassthru,
  setShouldPayeeDisbUpdate,
  onSuccess,
  sidebarIsOpen,
  setSidebarIsOpen
}: TPayeeDisbursements) => {
  const [record, setRecord] = useState<TRecord>({ id: null, contractId: accountId });

  const request = useMemo(() => ({ contractId: accountId }), [accountId]);

  const { data, isLoading } = useFetchData({
    request,
    setShouldPayeeDisbUpdate
  });

  const total: number = useMemo(() => data.length, [data]);

  const { paginationProps } = useNoCountPaginationProps(total, 20);

  const reloadData = useCallback(
    (isNew = false) => {
      onSuccess();
    },
    [onSuccess]
  );

  const { isOpen, setIsOpen, formWrapper } = useOpenSideBar({
    reloadData,
    payeesNumber: {
      primaryBuyerNumber,
      primarySellerNumber
    },
    customSidebarClass: "h-100",
    contractId: accountId,
    setRecord,
    record,
    isPassthru,
    sidebarIsOpen,
    setSidebarIsOpen
  });

  const openDisb = useCallback(
    (id: string) => {
      setRecord({ id });
      setIsOpen(true);
    },
    [setIsOpen, setRecord]
  );

  const { onRow } = useDefualtOnRow(openDisb);

  const { columns } = useGetTableColumns({
    setIsOpen,
    reloadData,
    setRecord,
    openDisb
  });

  const onAddClick = useCallback((): void => {
    setIsOpen(true);
  }, [setIsOpen]);

  const rowClassName = useCallback(
    (record: IPayeeDisbursements): string => (record.status === 3 || record.status === 2 ? "gray-row" : ""),
    []
  );

  useEffect(() => {
    shouldPayeeDisbUpdate && reloadData();
  }, [reloadData, shouldPayeeDisbUpdate]);

  return (
    <Row className="w-100 mb-4">
      <Card className="w-100">
        <Header data={data} isLoading={isLoading} onAddClick={onAddClick} />
        {isLoading ? (
          <Loader fontSize="64px" />
        ) : (
          data.length !== 0 && (
            <Row className="w-100 d-block">
              <Table
                dataSource={data}
                columns={columns}
                className="w-100 clickable-rows-table small-table"
                rowKey="id"
                pagination={paginationProps}
                rowClassName={rowClassName}
                onRow={onRow}
              />
            </Row>
          )
        )}
      </Card>
      {isOpen && createPortal(formWrapper, document.body)}
    </Row>
  );
};
